import { lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from "react";

import { StatusBlock } from "./StatusBlock.tsx";

/**
 * Una parte de la aplicacion que se descarga cuando se abre y no al entrar.
 *
 * El paquete entero pasaba de los 500 kB que Vite marca como tope, y ese tope
 * no se toca: lo que no se abre al entrar —la biblioteca, la configuracion, el
 * calendario, la semana— sale en su propio trozo y viaja la primera vez que se
 * pide. El service worker lo guarda igual que al resto (`/assets/*` va con hash
 * en el nombre; ver `sw-routes.ts`).
 *
 * El `catch` no es adorno. Tras un despliegue, una pestana que sigue abierta
 * con la version anterior pide un trozo que ya no existe en el servidor: sin
 * esto, `lazy` lanza, no hay limite de error encima y React desmonta la
 * aplicacion entera en blanco. Con esto, la parte que fallo se pinta como
 * cualquier otro fallo de carga, y "Reintentar" recarga la pagina, que es lo
 * que trae la version nueva.
 */
export function lazyPart<TProps extends object>(
  load: () => Promise<ComponentType<TProps>>,
): LazyExoticComponent<ComponentType<TProps>> {
  return lazy(async () => {
    try {
      return { default: await load() };
    } catch {
      return { default: ChunkFailed };
    }
  });
}

function ChunkFailed(): ReactNode {
  return (
    <StatusBlock
      error={new Error("Esta parte no se pudo descargar.")}
      onRetry={() => window.location.reload()}
    />
  );
}

/** Lo que se ve mientras el trozo viaja: el mismo esqueleto que el resto de la app. */
export function LoadingPart(): ReactNode {
  return <StatusBlock loading />;
}
